import React, {
    useContext, useCallback, useEffect, useMemo, useRef
} from 'react';
import _ from 'lodash';
import appConfig from 'app-config';
import { TranslatorContext } from '@jutro/locale';
import { readViewModelValue } from 'gw-jutro-adapters-react';
import { withRouter } from 'react-router-dom';
import {
    WizardPage,
    wizardProps,
    WizardPageTemplate,
    WizardPageTemplateWithTitle
} from 'gw-portals-wizard-react';
import { useValidation } from 'gw-portals-validation-react';
import { fnolCommonMessages } from 'gw-capability-fnol-common-react';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import FNOLCARepairFacilityPage from '../RepairFacility/RepairFacilityPage';
import messages from '../../FNOLCA.messages';
import metadata from './TypeOfRepairPage.metadata.json5';

const { env } = appConfig;

const repairFacility = {
    path: '/repair',
    component: FNOLCARepairFacilityPage,
    title: messages.wizardCARepairFacility,
    stepProps: {
        template: WizardPageTemplateWithTitle
    }
};

function FNOLCATypeOfRepairPage(props) {
    const translator = useContext(TranslatorContext);
    const {
        onValidate,
        isComponentValid,
        initialValidation,
        registerInitialComponentValidation
    } = useValidation('TypeOfRepairPage');
    const {
        wizardData: claimVM, updateWizardData, steps, changeNextSteps
    } = props;

    const repairerIconPath = useRef(undefined);

    const repairChoicesPreferredVendor = useCallback(() => {
        return (
            <React.Fragment>
                <div align="left">{translator(messages.approvedRepairerSecLabel)}</div>
                <ul>
                    <li align="left">{translator(messages.approvedRepairerSecLabelLiOne)}</li>
                    <li align="left">{translator(messages.approvedRepairerSecLabelLiThree)}</li>
                    <li align="left">{translator(messages.approvedRepairerSecLabelLiFour)}</li>
                    <li align="left">{translator(messages.approvedRepairerSecLabelLiFive)}</li>
                </ul>
                <div align="left">{translator(messages.approvedRepairerSecLabelLast)}</div>
            </React.Fragment>
        );
    }, [translator]);

    // TODO lino change for prod 
    // Repair Tracker - Images Retrieval
    const getRepairIconPath = useCallback(() => {
        const prefix = _.get(env, 'DEPLOYMENT_PATH', '').replace(/\/$/, '');
        repairerIconPath.current = `${prefix}/repairers`;
        //repairerIconPath.current = `/repairers`;
    }, []);

    useEffect(() => {
        const repairOptionPath = 'lobs.commercialAuto.repairOption';
        const repairOptionVIPath = 'lobs.commercialAuto.repairOption.vehicleIncident';
        const vehicleIncidentsPath = 'lobs.commercialAuto.vehicleIncidents';
        const vehicleIncidents = _.get(claimVM.value, vehicleIncidentsPath, []);
        if (!_.get(claimVM.value, repairOptionPath)) {
            _.set(claimVM.value, repairOptionPath, {
                repairOptionChoice: null,
                repairFacility: null,
                vehicleIncident: null
            });
        }
        if (!_.get(claimVM.value, repairOptionVIPath) && vehicleIncidents.length === 1) {
            _.set(claimVM.value, repairOptionVIPath, vehicleIncidents[0]);
        } else if (!_.get(claimVM.value, repairOptionVIPath) && vehicleIncidents.length > 1) {
            _.set(claimVM.value, repairOptionVIPath,
                _.find(vehicleIncidents, (inc) => inc.vehicleLossParty_Ext === 'insured' && inc.driver != null));
        }
        getRepairIconPath();
        updateWizardData(claimVM);
        // Do not add in dependencies here as we only need to load the above information once
        // Adding these dependencies will cause a constant reload
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const repairPageHandling = useCallback(() => {
        const typeOfRepairPageIndex = _.findIndex(steps, { path: '/type-of-repair' });
        const repairPageIndex = typeOfRepairPageIndex + 1;
        if (steps[repairPageIndex].path === '/repair') {
            steps.splice(repairPageIndex, 1);
        }
        const remainingSteps = steps.slice(repairPageIndex, steps.length);
        let newSteps = [];
        switch (_.get(claimVM.value, 'lobs.commercialAuto.repairOption.repairOptionChoice')) {
            case 'PreferredVendor':
                newSteps = remainingSteps;
                break;
            case 'NewVendor':
                newSteps = [repairFacility, ...remainingSteps];
                break;
            case 'NoRepair':
                newSteps = remainingSteps;
                break;
            default:
        }
        if (newSteps.length > 0) {
            changeNextSteps(newSteps);
        }
        return claimVM;
    }, [changeNextSteps, claimVM, steps]);

    useEffect(() => {
        repairPageHandling();
        // execute once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onNext = () => {
        repairPageHandling();
        return claimVM;
    };

    const handleChange = useCallback(
        (value, path) => {
            const oldRepairOptionObject = _.get(
                claimVM.value,
                'lobs.commercialAuto.repairOption.repairOptionChoice'
            );
            const oldValue = !_.isEmpty(oldRepairOptionObject) ? oldRepairOptionObject.code : null;
            if (oldValue != null
                    && value !== oldValue) {
                _.set(claimVM.value, 'lobs.commercialAuto.repairOption.ownRepairer.value', null);
            }
            _.set(claimVM.value, path, value);
            updateWizardData(claimVM);
        },
        [claimVM, updateWizardData]
    );

    const getIncidentVehicleName = () => {
        const vehicleIncidentsPath = 'lobs.commercialAuto.vehicleIncidents';
        const vehicleIncidents = _.get(claimVM.value, vehicleIncidentsPath, []);
        const insuredVehicleincident = _.find(vehicleIncidents, (inc) => inc.vehicleLossParty_Ext === 'insured'
                                                && inc.driver != null);
        return !_.isEmpty(insuredVehicleincident) ? insuredVehicleincident.vehicle.licensePlate : 'Insured vehicle';
    };

    const setTypeOfRepair = (value) => {
        _.set(claimVM.value, 'lobs.commercialAuto.repairOption.repairOptionChoice', value);
        updateWizardData(claimVM);
    };

    const overrideProps = {
        typeOfRepairPageHeader: {
            content: translator(messages.preferredRepairChoice, { licensePlate: getIncidentVehicleName() })
        },
        repairChoicesRadioButton: {
            value: _.get(claimVM.value, 'lobs.commercialAuto.repairOption.repairOptionChoice'),
            onValueChange: (value) => {
                setTypeOfRepair(value);
            },
            availableValues: [
                {
                    id:'ch-fnol-repair-PreferredVendor-button',
                    code: 'PreferredVendor',
                    displayName: messages.approvedRepairer,
                    iconSrc: `${repairerIconPath.current}/car.svg`,
                    secondaryLabel: repairChoicesPreferredVendor()
                },
                {
                    id:'ch-fnol-repair-NewVendor-button',
                    code: 'NewVendor',
                    displayName: messages.ownRepairer,
                    iconName: 'mi-directions-car',
                    secondaryLabel: messages.ownRepairerSecLabel
                },
                {
                    id:'ch-fnol-repair-NoRepair-button  ',
                    code:'NoRepair',
                    displayName: messages.noRepairer,
                    iconName: 'mi-not-interested',
                    secondaryLabel: messages.noRepairerSecLabel
                }
            ],
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            handleChange: handleChange
        }
    };

    const readValue = useCallback(
        (id, path) => {
            return readViewModelValue(metadata.pageContent, claimVM, id, path, overrideProps);
        },
        [claimVM, overrideProps]
    );

    const shouldPageSkip = useCallback(() => {
        const repairOptionChoice = _.get(
            claimVM.value,
            'lobs.commercialAuto.repairOption.repairOptionChoice'
        );
        return !_.isEmpty(repairOptionChoice);
    }, [claimVM]);

    useEffect(() => {
        registerInitialComponentValidation(shouldPageSkip);
    }, [registerInitialComponentValidation, shouldPageSkip]);

    return (
        <WizardPage
            skipWhen={initialValidation}
            disableNext={!isComponentValid}
            nextButtonID={translator(messages.fnolRepairPageNextButton)}
            onNext={onNext}
            template={WizardPageTemplate}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={claimVM}
                resolveValue={readValue}
                overrideProps={overrideProps}
                onValidationChange={onValidate}
                onModelChange={updateWizardData}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
            />
        </WizardPage>
    );
}

FNOLCATypeOfRepairPage.propTypes = wizardProps;
export default withRouter(FNOLCATypeOfRepairPage);
